'use client';

/**
 * SurfaceHost — renders an opened tool/space beneath the decks.
 * id format: '<tool>:<projectSlug>' for project tools, or a global id
 * ('inbox', 'portfolio', 'crm', 'brain', …).
 * Every surface pulls from the SAME APIs as the classic UI (coverage ledger:
 * docs/UI-FUTURE-COVERAGE.md — no element left behind).
 */
import { getBusinessProject, BUSINESS_PROJECTS } from '@/lib/business-projects';

// Domains explicitly claimed by a non-factory project (Ivan→industrial.film,
// Charters→yachts, Olga→olga sites, …). The 'factory' project (WTF Ventures)
// must NOT show these — each site appears only under its real owner, matching
// Classic UI grouping (Boss 2026-07-16).
const CLAIMED_DOMAINS: string[] = BUSINESS_PROJECTS
  .filter(p => Array.isArray(p.sites))
  .flatMap(p => (p.sites as { domain: string }[]).map(s => s.domain));
import { BIZ_COLOR } from '@/future/shell/decks';
import { mono, display } from '@/future/primitives';
import { PerformanceSurface, PerfHome } from './Performance';
import { FunnelSurface } from './Funnel';
import { InboxSurface } from './Inbox';
import { InvestorsSurface } from './Investors';
import { PortfolioSurface } from './Portfolio';
import { CrmSurface } from './Crm';
import { ImproveSurface } from './Improve';
import { AbTestsSurface } from './AbTests';
import { AdsSurface } from './Ads';
import { AdsParitySurface } from './AdsParity';
import { AdsDeckSurface } from './AdsDeck';
import { CrmDeckSurface } from './CrmDeck';
import { PerfDeckSurface } from './PerfDeck';
import { ContentDeckSurface } from './ContentDeck';
import { FactoryDeckSurface } from './FactoryDeck';
import { InvDeckSurface } from './InvDeck';
import { AbDeckSurface } from './AbDeck';
import { VideoDeckSurface } from './VideoDeck';
import { FactorySurface } from './Factory';
import { ComposeSurface, ContactsSurface, SendersSurface } from './EmailOps';
import { BrainSurface, SessionsSurface } from './Brain';
import { NetworkSurface } from './Network';
import { WebsitesSurface } from './Websites';
import { SettingsSurface } from './SettingsSurface';
import { PendingSurface } from './Pending';
import { ChatSurface } from './ChatSurface';
import { ContentSuiteSurface } from './ContentSuite';
import { VideoLabSurface } from './VideoLab';

type View = { deck: string } | { surface: string };

export function SurfaceHost({ id, navigate, back }: { id: string; navigate: (v: View) => void; back: () => void }) {
  const [tool, slug] = id.includes(':') ? id.split(':') : [id, ''];
  const project = slug ? getBusinessProject(slug) : null;
  const color = project ? BIZ_COLOR[project.slug.startsWith('collab-') ? 'collabs' : project.slug] : 'var(--uif-cy-interactive)';

  const title = project ? `${project.name.toUpperCase()} · ${tool.replace('-', ' ').toUpperCase()}` : tool.toUpperCase();

  // header chips — classic project header links parity (Pitch site, Sites hub, Live funnel…)
  const chips: { label: string; href: string }[] = project ? [
    ...(project.funnelUrl ? [{ label: 'LIVE FUNNEL', href: project.funnelUrl }] : []),
    ...(project.links ?? []),
  ] : [];

  const body = (() => {
    switch (tool) {
      /* v6 tool mapping: PERFORMANCE → LADDER↔FOCUS toggle (Boss 2026-07-10 experiment —
         revert = delete PerfHome + restore this case to PerfDeckSurface; tag pre-perf-ladder-experiment) */
      case 'performance': return <PerfHome project={project} deckDomains={project && project.sites !== 'factory' ? project.sites.map(s => s.domain) : undefined} />;
      case 'performance-classic': return <PerformanceSurface project={project} />;
      case 'overview':
      case 'funnel': return <FunnelSurface project={project} />;
      case 'inbox': return <InboxSurface scope={null} onCompose={() => navigate({ surface: 'compose' })} />;
      case 'email': return <InboxSurface scope={project?.emailKeys ?? null} scopeName={project?.name} onCompose={() => navigate({ surface: slug ? `compose:${slug}` : 'compose' })} />;
      /* v6 one-focus decks (Design round 3) — real APIs, classic surfaces kept at *-classic */
      case 'investors': return <InvDeckSurface />;
      case 'investors-classic': return <InvestorsSurface />;
      case 'portfolio': return <PortfolioSurface />;
      case 'crm': return <CrmDeckSurface sites={project && project.sites !== 'factory' ? project.sites.map(s => s.domain) : undefined} scopeName={project?.name} />;
      case 'crm-classic': return <CrmSurface />;
      case 'improve': return <ImproveSurface scope={project && project.sites !== 'factory' ? project.sites.map(s => s.domain) : undefined} exclude={project && project.sites === 'factory' ? CLAIMED_DOMAINS : undefined} scopeName={project?.name} />;
      case 'perf-deck': return <PerfDeckSurface domains={project && project.sites !== 'factory' ? project.sites.map(s => s.domain) : undefined} />;
      case 'content-deck': return <ContentDeckSurface />;
      case 'factory-deck': return <FactoryDeckSurface />;
      case 'ab-tests': return <AbDeckSurface funnelId={project?.funnelId} />;
      case 'ab-classic': return <AbTestsSurface project={project} />;
      /* decision deck is the front door (Boss 2026-07-10); dashboard wall lives at ads-full */
      case 'ads': return <AdsDeckSurface project={project} openFull={() => navigate({ surface: slug ? `ads-full:${slug}` : 'ads-full' })} />;
      case 'ads-full': return <AdsParitySurface project={project} />; // v6 parity room (Design round 3) — real APIs
      case 'ads-deck': return <AdsSurface project={project} />;
      case 'factory': return <FactoryDeckSurface />;
      case 'factory-classic': return <FactorySurface />;
      case 'video-lab': return <VideoDeckSurface funnelId={project?.funnelId} slug={project?.slug} />;
      case 'video-classic': return <VideoLabSurface project={project} />;
      case 'compose': return <ComposeSurface scope={Array.isArray(project?.emailKeys) ? project.emailKeys : null} />;
      case 'contacts': return <ContactsSurface scope={Array.isArray(project?.emailKeys) ? project.emailKeys : null} />;
      case 'senders': return <SendersSurface />;
      case 'brain': return <BrainSurface />;
      case 'sessions': return <SessionsSurface navigate={navigate} />;
      case 'network': return <NetworkSurface navigate={navigate} />;
      case 'websites': return <WebsitesSurface project={project} />;
      /* v6: project content tab → one-focus queue deck; hangar card keeps the station suite */
      case 'content': return slug ? <ContentDeckSurface /> : <ContentSuiteSurface />;
      case 'content-suite': return <ContentSuiteSurface />;
      case 'chat': return <ChatSurface />;
      case 'settings': return <SettingsSurface />;
      default: return <PendingSurface tool={tool} />;
    }
  })();

  return (
    <div className="absolute inset-0 flex flex-col" style={{ padding: 'var(--uif-stage-pad)', paddingTop: 148, paddingBottom: 72 }} /* clear doubled-font chrome (logo+chip ≈y110) and bottom legends — Boss 2026-07-10: nothing lays over anything */>
      {/* room header — plain label of the current tool (v4: no pill rows) */}
      <div className="flex items-center justify-center gap-3 pb-4 flex-shrink-0">
        <div className="h-px w-16" style={{ background: `linear-gradient(90deg, transparent, ${color})` }} />
        <div style={{ ...mono(18, '.42em'), color: 'var(--uif-t4)' }}>{title}</div>
        {chips.map(c => (
          <a key={c.href} href={c.href} target="_blank" rel="noreferrer"
            style={{ ...mono(14, '.18em'), color, border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`, background: `color-mix(in srgb, ${color} 8%, transparent)`, borderRadius: 999, padding: '3px 10px', textDecoration: 'none' }}>
            {c.label.toUpperCase()} ↗
          </a>
        ))}
        <div className="h-px w-16" style={{ background: `linear-gradient(90deg, ${color}, transparent)` }} />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>{body}</div>
    </div>
  );
}
